import React from "react";
import "./TTTAcademy.css";
import { Card, CardContainer } from "../../../components/Card";
import useSWR from "swr";
import API_ENDPOINTS from "../../../apis/endpoints";
import { BarChart, LineChart } from "../../../components/Charts";
import {
    Checkbox,
    CircularProgress,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Typography,
} from "@mui/material";
import LastRefreshed from "../LastRefreshed";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 10.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

export default function TTTAcademy() {
    const {
        data: weekDataSet,
        error: weekError,
        isLoading: weekIsLoading,
    } = useSWR(`${API_ENDPOINTS.ACADEMY_WEEKS}`);
    const { data: weekData, refreshed_at } = weekDataSet ?? {};

    if (weekIsLoading) {
        return <CircularProgress />;
    }

    return <WeekDisplay weeks={weekData} refreshed_at={refreshed_at} />;
}

function WeekDisplay({ weeks, refreshed_at }) {
    const [selectedWeeks, setSelectedWeeks] = React.useState(
        weeks.slice(-6).map((week) => week.key)
    );

    const handleWeekChange = (event) => {
        setSelectedWeeks(event.target.value);
    };
    return (
        <Card>
            <div className="header-section">
                <Typography variant="highlightSemibold">Academy</Typography>
                <FormControl sx={{ m: 1, width: 300 }}>
                    <InputLabel id="week-multi-select-label">Weeks</InputLabel>
                    <Select
                        labelId="week-multi-select-label"
                        id="week-multi-select"
                        multiple
                        value={selectedWeeks}
                        onChange={handleWeekChange}
                        input={<OutlinedInput label="All Weeks" />}
                        renderValue={(selected) =>
                            weeks.length === selectedWeeks.length
                                ? "All weeks"
                                : weeks
                                      ?.filter((week) =>
                                          selected.includes(week.key)
                                      )
                                      .map((week) => week.name)
                                      .join(", ")
                        }
                        MenuProps={MenuProps}
                    >
                        {weeks?.map((week) => (
                            <MenuItem key={week.key} value={week.key}>
                                <Checkbox
                                    checked={
                                        selectedWeeks.indexOf(week.key) > -1
                                    }
                                />
                                <ListItemText primary={week.name} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            <SelectedWeeks weeks={selectedWeeks} />
            <LastRefreshed date={refreshed_at} />
        </Card>
    );
}

function SelectedWeeks({ weeks }) {
    const {
        data: academyData,
        error: academyError,
        isLoading: academyIsLoading,
    } = useSWR(`${API_ENDPOINTS.ACADEMY_DATA}?weeks=${weeks.join(",")}`);

    if (academyIsLoading) {
        return <CircularProgress />;
    }

    return (
        <div className="cards-container">
            <CardContainer columns={2}>
                <BarChart
                    data={academyData}
                    series={[
                        { name: "Total Revenue", key: "total_revenue_inr" },
                    ]}
                    xLabel="Week"
                    xKey="week"
                    yLabel="Revenue"
                    title="Total Overall Revenue"
                />
                <LineChart
                    data={academyData}
                    series={[
                        { name: "Total Seats Sold", key: "total_seats_sold" },
                    ]}
                    xLabel="Week"
                    xKey="week"
                    yLabel="Seats Sold"
                    title="Total Seats Sold"
                />
            </CardContainer>
            <CardContainer columns={2}>
                <BarChart
                    data={academyData}
                    series={[
                        {
                            name: "Writing That Sells Revenue",
                            key: "writing_that_sells_revenue_inr",
                        },
                    ]}
                    xLabel="Week"
                    xKey="week"
                    yLabel="Revenue"
                    title="Writing That Sells Revenue"
                />
                <LineChart
                    data={academyData}
                    series={[
                        {
                            name: "Writing That Sells Seats Sold",
                            key: "writing_that_sells_seats_sold",
                        },
                    ]}
                    xLabel="Week"
                    xKey="week"
                    yLabel="Seats Sold"
                    title="Writing That Sells Seats Sold"
                />
            </CardContainer>
            <CardContainer columns={2}>
                <BarChart
                    data={academyData}
                    series={[
                        {
                            name: "Design That Sells Revenue",
                            key: "design_that_sells_revenue_inr",
                        },
                    ]}
                    xLabel="Week"
                    xKey="week"
                    yLabel="Revenue"
                    title="Design That Sells Revenue"
                />
                <LineChart
                    data={academyData}
                    series={[
                        {
                            name: "Design That Sells Seats Sold",
                            key: "design_that_sells_seats_sold",
                        },
                    ]}
                    xLabel="Week"
                    xKey="week"
                    yLabel="Seats Sold"
                    title="Design That Sells Seats Sold"
                />
            </CardContainer>
            <CardContainer columns={2}>
                <BarChart
                    data={academyData}
                    series={[
                        {
                            name: "Writing That Sells Pro Revenue",
                            key: "writing_that_sells_pro_revenue_inr",
                        },
                    ]}
                    xLabel="Week"
                    xKey="week"
                    yLabel="Revenue"
                    title="Writing That Sells Pro Revenue"
                />
                <LineChart
                    data={academyData}
                    series={[
                        {
                            name: "Writing That Sells Pro Seats Sold",
                            key: "writing_that_sells_pro_seats_sold",
                        },
                    ]}
                    xLabel="Week"
                    xKey="week"
                    yLabel="Seats Sold"
                    title="Writing That Sells Pro Seats Sold"
                />
            </CardContainer>
            <CardContainer columns={2}>
                <BarChart
                    data={academyData}
                    series={[
                        {
                            name: "Landing Pages Traffic",
                            key: "landing_pages_traffic",
                        },
                    ]}
                    xLabel="Week"
                    xKey="week"
                    yLabel="Traffic"
                    title="Landing Pages Traffic"
                />
                <LineChart
                    data={academyData}
                    series={[
                        {
                            name: "Landing Pages Conversion",
                            key: "landing_pages_conversion",
                        },
                    ]}
                    xLabel="Week"
                    xKey="week"
                    yLabel="Conversion"
                    title="Landing Pages Conversion"
                />
            </CardContainer>
        </div>
    );
}
